/**
 * 🌦️ WeatherLayerManager - Comprehensive Weather Overlay Management
 * Handles forecast time-stepping, dynamic legends, One Call API, and animations
 * For Dashboard Map + Crisis Details Map (Desktop Only)
 */

// Weather layer definitions with OpenWeatherMap 2.0 support
export const WEATHER_LAYERS = {
  precipitation: {
    id: 'precipitation',
    name: 'Precipitation',
    currentLayer: 'PA0',
    forecastLayer: 'PA0',
    unit: 'mm/h',
    colorScale: [
      { value: 0, color: 'transparent' },
      { value: 0.1, color: '#b3e5fc' },
      { value: 0.5, color: '#4fc3f7' },
      { value: 1, color: '#29b6f6' },
      { value: 2, color: '#0288d1' },
      { value: 5, color: '#0277bd' },
      { value: 10, color: '#01579b' }
    ]
  },
  clouds: {
    id: 'clouds',
    name: 'Cloud Cover',
    currentLayer: 'CL',
    forecastLayer: 'CL',
    unit: '%',
    colorScale: [
      { value: 0, color: 'transparent' },
      { value: 25, color: '#B8E6FF' },
      { value: 50, color: '#7BB3FF' },
      { value: 75, color: '#4D94FF' },
      { value: 100, color: '#1E4D8C' }
    ]
  },
  wind: {
    id: 'wind',
    name: 'Wind Speed',
    currentLayer: 'WND',
    forecastLayer: 'WND',
    unit: 'm/s',
    colorScale: [
      { value: 0, color: 'transparent' },
      { value: 1, color: '#f0f0f0' },
      { value: 3, color: '#d0d0d0' },
      { value: 5, color: '#909090' },
      { value: 10, color: '#606060' },
      { value: 15, color: '#404040' },
      { value: 20, color: '#202020' }
    ]
  },
  temperature: {
    id: 'temperature',
    name: 'Temperature',
    currentLayer: 'TA2',
    forecastLayer: 'TA2',
    unit: '°C',
    colorScale: [
      { value: -30, color: '#1a237e' },
      { value: -10, color: '#3f51b5' },
      { value: 0, color: '#2196f3' },
      { value: 10, color: '#4caf50' },
      { value: 20, color: '#ffeb3b' },
      { value: 30, color: '#ff9800' },
      { value: 40, color: '#f44336' }
    ]
  },
  pressure: {
    id: 'pressure',
    name: 'Pressure',
    currentLayer: 'APM',
    forecastLayer: 'APM',
    unit: 'hPa',
    colorScale: [
      { value: 980, color: '#1a237e' },
      { value: 990, color: '#3f51b5' },
      { value: 1000, color: '#2196f3' },
      { value: 1010, color: '#4caf50' },
      { value: 1020, color: '#ffeb3b' },
      { value: 1030, color: '#ff9800' },
      { value: 1040, color: '#f44336' }
    ]
  }
};

// Time step configuration
export const TIME_STEPS = {
  current: { label: 'Now', offset: 0 },
  plus3h: { label: '+3h', offset: 3 },
  plus6h: { label: '+6h', offset: 6 }
};

export class WeatherLayerManager {
  constructor(map, onLegendUpdate = null, onTimeStepChange = null) {
    this.map = map;
    this.onLegendUpdate = onLegendUpdate;
    this.onTimeStepChange = onTimeStepChange;
    
    // State management
    this.activeLayers = [];
    this.currentTimeStep = 'current';
    this.weatherLayersRef = {};
    this.animationInterval = null;
    this.isAnimating = false;
    
    // API configuration
    this.apiKey = process.env.REACT_APP_OPENWEATHERMAP_API_KEY;
    
    if (!this.apiKey) {
      console.error('🌦️ OpenWeatherMap API key not found');
    }
  }

  /**
   * Initialize the weather layer manager
   */
  initialize() {
    if (!this.isSupported) {
      console.error('🌦️ Weather layers not supported - missing requirements');
      return false;
    }

    return true;
  }

  /**
   * Create a weather layer for a specific type and time step
   */
  createWeatherLayer(layerId, timeStep) {
    const layer = WEATHER_LAYERS[layerId];
    if (!layer) {
      console.error('🌦️ Weather layer not found:', layerId);
      return null;
    }

    const openweathermapLayer = timeStep === 'current' ? layer.currentLayer : layer.forecastLayer;

    // Calculate date parameter for forecasts (Weather Maps 2.0)
    let dateParam = '';
    const now = new Date();
    
    if (timeStep === 'current') {
      // Weather Maps 2.0 might require date parameter even for current weather
      dateParam = `&date=${Math.floor(now.getTime() / 1000)}`;
    } else {
      // Forecast times
      const timeStepConfig = TIME_STEPS[timeStep];
      if (timeStepConfig && timeStepConfig.offset) {
        const forecastTime = new Date(now.getTime() + (timeStepConfig.offset * 60 * 60 * 1000));
        dateParam = `&date=${Math.floor(forecastTime.getTime() / 1000)}`;
      }
    }

    // Custom palette for better visibility (clouds use blue scale instead of grey)
    let paletteParam = '';
    if (layerId === 'clouds') {
      // Blue-scale palette for better contrast against map background
      paletteParam = '&palette=0:00000000;25:87CEEB;50:4169E1;75:0000FF;100:000080';
    }

    const imageMapType = new window.google.maps.ImageMapType({
      getTileUrl: (coord, zoom) => {
        const tileUrl = `https://maps.openweathermap.org/maps/2.0/weather/${openweathermapLayer}/${zoom}/${coord.x}/${coord.y}?appid=${this.apiKey}${dateParam}${paletteParam}`;
        return tileUrl;
      },
      tileSize: new window.google.maps.Size(256, 256),
      maxZoom: 10,
      minZoom: 1,
      name: layer.name,
      opacity: layerId === 'clouds' ? 0.6 : 0.99
    });

    return imageMapType;
  }

  /**
   * Attach weather layers to the map
   */
  attachWeatherLayers(layerTypes = []) {
    // Clear existing layers first
    this.detachAllWeatherLayers();

    // Update active layers (even if empty array)
    this.activeLayers = [...layerTypes];
    
    if (layerTypes && layerTypes.length > 0) {
      // Create and attach new layers
      layerTypes.forEach(layerType => {
        const layer = this.createWeatherLayer(layerType, this.currentTimeStep);
        
        if (layer) {
          this.map.overlayMapTypes.push(layer);
          this.weatherLayersRef[`${layerType}_${this.currentTimeStep}`] = layer;
        } else {
          console.error('🌦️ Failed to create weather layer:', layerType);
        }
      });
    }

    // Always update legend (will hide if no layers, show if layers exist)
    this.updateLegend();
  }

  /**
   * Remove all weather layers from the map
   */
  detachAllWeatherLayers() {
    if (!this.map) return;

    try {
      // Remove all overlay map types (weather layers)
      while (this.map.overlayMapTypes.length > 0) {
        this.map.overlayMapTypes.removeAt(0);
      }

      // Clear references
      this.weatherLayersRef = {};

    } catch (error) {
      console.error('🌦️ Error detaching weather layers:', error);
    }
  }

  /**
   * Change time step and refresh layers
   */
  setTimeStep(timeStep) {
    if (!TIME_STEPS[timeStep]) {
      console.warn(`🌦️ Invalid time step: ${timeStep}`);
      return;
    }

    this.currentTimeStep = timeStep;
    

    // Re-attach layers with new time step
    if (this.activeLayers.length > 0) {
      this.attachWeatherLayers(this.activeLayers);
    }

    // Notify time step change
    if (this.onTimeStepChange) {
      this.onTimeStepChange(timeStep);
    }
  }

  /**
   * Start animation cycling through time steps
   */
  startAnimation() {
    if (this.isAnimating || this.activeLayers.length === 0) return;

    this.isAnimating = true;
    const timeSteps = Object.keys(TIME_STEPS);
    let currentIndex = 0;

    this.animationInterval = setInterval(() => {
      const timeStep = timeSteps[currentIndex];
      this.setTimeStep(timeStep);
      
      currentIndex = (currentIndex + 1) % timeSteps.length;
    }, 5000); // 5-second intervals for better analysis time


  }

  /**
   * Stop animation
   */
  stopAnimation() {
    if (this.animationInterval) {
      clearInterval(this.animationInterval);
      this.animationInterval = null;
    }
    this.isAnimating = false;
    console.log('🌦️ Weather animation stopped');
  }

  /**
   * Update legend display with active layers
   */
  updateLegend() {
    if (!this.onLegendUpdate) {
      return;
    }
    
    const legends = this.activeLayers.map(layerType => {
      const layer = WEATHER_LAYERS[layerType];
      
      if (!layer) {
        console.error('🌦️ No layer definition found for:', layerType);
        return null;
      }
      
      if (!layer.colorScale) {
        console.error('🌦️ No colorScale found for layer:', layerType);
        return null;
      }
      
      return {
        type: layerType,
        name: layer.name,
        unit: layer.unit,
        items: layer.colorScale.map(scale => ({
          color: scale.color,
          value: scale.value,
          label: `${scale.value}${layer.unit}`
        }))
      };
    }).filter(legend => legend !== null);

    this.onLegendUpdate(legends);
  }

  /**
   * Get weather forecast for a specific location using One Call API
   */
  async getLocationWeather(lat, lon) {
    if (!this.apiKey) {
      throw new Error('OpenWeatherMap API key not configured');
    }

    try {
      const response = await fetch(
        `https://api.openweathermap.org/data/3.0/onecall?lat=${lat}&lon=${lon}&appid=${this.apiKey}&units=metric`
      );

      if (!response.ok) {
        throw new Error(`Weather API error: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        current: {
          temperature: Math.round(data.current.temp),
          description: data.current.weather[0].description,
          icon: data.current.weather[0].icon,
          windSpeed: Math.round(data.current.wind_speed * 3.6), // m/s to km/h
          humidity: data.current.humidity,
          pressure: data.current.pressure
        },
        hourly: data.hourly.slice(0, 6).map(hour => ({
          time: new Date(hour.dt * 1000).toLocaleTimeString('en-AU', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          temperature: Math.round(hour.temp),
          description: hour.weather[0].description,
          icon: hour.weather[0].icon,
          precipitation: hour.pop * 100 // Probability of precipitation
        })),
        alerts: data.alerts || []
      };
    } catch (error) {
      console.error('🌦️ Error fetching weather data:', error);
      throw error;
    }
  }

  /**
   * Cleanup method
   */
  destroy() {
    this.stopAnimation();
    this.detachAllWeatherLayers();

  }

  // Getters
  get isSupported() {
    const hasMap = !!this.map;
    const hasGoogle = !!window.google;
    const hasApiKey = !!this.apiKey;
    
    return hasMap && hasGoogle && hasApiKey;
  }

  get currentActiveLayers() {
    return [...this.activeLayers];
  }

  get currentTimeStepInfo() {
    return TIME_STEPS[this.currentTimeStep];
  }

  get isPlaying() {
    return this.isAnimating;
  }
}

export default WeatherLayerManager; 
